import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useAuth } from '../../contexts/AuthContext'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import DocumentsSection from '../../components/retailer-shop/DocumentsSection'
import PageTitle from '../../components/common/PageTitle'
import VerificationStep from '../../components/Verification/VerificationStep'
import PayForVerificationButton from '../../components/Verification/PayForVerificationButton'
import ShopDetails from '../../components/Verification/ShopDetails'
import { useState } from 'react'
import VerificationPaymentModal from '../../components/Verification/VerificationPaymentModal'
import { PAYMENT_ID_KEY, paymentStatus } from '../../constants/payment'
import StatusPill from '../../components/Verification/StatusPill'
import { formatTime } from '../../utils/date.util'
import VerificationPaymentNotification from '../../components/Verification/VerificationPaymentNotification'
import { VERIFICATION_STATUSES } from '../../constants/verificationStatuses'
import VerificationStatusMessage from '../../components/Verification/VerificationStatusMessage'

const ShopVerificationPage = () => {
  const { userType, currentUser } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: shopData, isLoading: loadingShop } = useQuery({
    queryKey: QueryKeys.myShopDetails(userType),
    queryFn: () => handleGetRequest(`${userType}s/my-shop`),
  })

  const { data: shopVerificationData, isLoading: loadingShopVerification } = useQuery({
    queryKey: QueryKeys.shopVerificationDetails(currentUser.id),
    queryFn: () => handleGetRequest(`${userType}s/my-verification`),
  })

  if (loadingShop || loadingShopVerification) {
    return <LoadingSpinner />
  }

  const shop = shopData?.data
  const verification = shopVerificationData?.data

  const isShopDetailsComplete =
    shop?.buildingName && shop?.shopPicture && shop?.shopName && shop?.floor
  const userPaymentStatus = verification?.payment?.status || ''
  const paymentDetails = verification?.payment

  const hasPendingPayment = () => localStorage.getItem(PAYMENT_ID_KEY)

  const renderContent = () => {
    if (hasPendingPayment()) {
      return (
        <VerificationPaymentNotification
          onClose={() => {}}
          onRetryPayment={() => setIsModalOpen(true)}
        />
      )
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 space-y-8">
      <div className="flex items-center gap-1">
        <PageTitle>Shop Verification</PageTitle>
        <StatusPill status={verification?.status} />
      </div>

      <VerificationStatusMessage status={verification?.status} />

      <VerificationStep title="Step 1: Shop Details" isComplete={!!isShopDetailsComplete}>
        <ShopDetails shop={shop} isComplete={isShopDetailsComplete} />
      </VerificationStep>

      <VerificationStep
        title="Step 2: Upload Documents"
        isComplete={verification?.hasUploadedAllRequiredDocuments}
        isPending={
          verification?.hasUploadedAllRequiredDocuments && !verification?.hasAllDocsApproved
        }
      >
        <DocumentsSection
          hasAllDocsApproved={verification?.hasAllDocsApproved}
          hasAllRequiredDocumentsUploaded={verification?.hasUploadedAllRequiredDocuments}
          readOnly={[
            VERIFICATION_STATUSES.approved.value,
            VERIFICATION_STATUSES.in_review.value,
          ].includes(verification?.status)}
        />
      </VerificationStep>

      <VerificationStep
        title="Step 3: Pay for Verification"
        isComplete={userPaymentStatus === paymentStatus.COMPLETED}
      >
        {renderContent()}
        <VerificationPaymentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

        {!hasPendingPayment() && userPaymentStatus !== paymentStatus.COMPLETED && (
          <PayForVerificationButton
            onClick={() => setIsModalOpen(true)}
            isDisabled={!isShopDetailsComplete}
          />
        )}

        {userPaymentStatus === paymentStatus.COMPLETED && (
          <div className="mt-4 p-4 bg-neutral-200-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Amount</p>
                <p className="font-medium">{paymentDetails?.amountText}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">{formatTime(paymentDetails?.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Method</p>
                <p className="font-medium">{paymentDetails?.paymentMethod}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium capitalize">{paymentDetails?.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Transaction Code</p>
                <p className="font-medium">{paymentDetails?.transactionCode}</p>
              </div>
            </div>
          </div>
        )}
      </VerificationStep>
    </div>
  )
}
export default ShopVerificationPage
